import React from "react";
import DividerBlock from "../../../divider-block";
import { Alert, AutoComplete, Checkbox } from "antd";
import FieldBlock from '../../../field-block';
import { Col, Row } from "antd";
import { Button } from "antd";
import { uuidHelper } from "../../../../helpers/uuid.helper";
import { useState, useRef } from "react";
import SelectField from "../../../select-field";
import NumberInputField from "../../../number-input-field";
import { useEffect } from "react";
import i18n from "../../../../i18n";
import { crossDockService } from "../../../../services/cross-dock.services";

// Опции для выбора типа упаковки
const packageTypeOptions = [
  { Id: 2, Name: 'Палета' }
];

export class Item {
  #distCenterId

  constructor() {
    this.uuid = uuidHelper.generateUUID();
    this.storeId = null;
    this.name = "";
    this.weight = "";
    this.volume = "";
    this.price = "";
    this.quantity = "";
    this.presetId = null;
    this.groupUuid = null;
    this.#distCenterId = null;
  }

  set distCenterId(id) {
    this.#distCenterId = id;
  }

  getDistCenterId()
  {
    return this.#distCenterId;
  }
}

const createGroup = () => ({
  groupUuid: uuidHelper.generateUUID(),
  name: "",
  presetId: null,
  selectedStoreIds: [],
  weight: "",
  volume: "",
  price: "",
  packageTypeId: null,
  quantity: "",
});

export default function Items({
  storesList,
  items,
  setItems,
  fieldsErrors,
  dividerStyle,
  legalEntityId}
) {

  const [reindexedStores, setReindexedStores] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [nomenclatureGroups, setNomenclatureGroups] = useState([]);
  const [presetOptions, setPresetOptions] = useState({});
  const [openDropdown, setOpenDropdown] = useState({});
  const debounceRefs = useRef({});
  const itemsRef = useRef(items);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
      let newReindexed = {};
      storesList.forEach((item) => {
        newReindexed[item.Id] = {
          'DistCenterId': item.DistCenterId,
        };
      });
      setReindexedStores(newReindexed);
  }, [storesList]);

  useEffect(() => {
      let distCenterIds = {};
      items.forEach((item) => {
        if (item && item.distCenterId) {
          distCenterIds[item.distCenterId] = true;
        }
      });
      if(Object.keys(distCenterIds).length > 1) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
  }, [items]);

  // Синхронизация nomenclatureGroups → items
  useEffect(() => {
    const currentItems = itemsRef.current;
    const newItems = [];
    const itemMap = new Map();

    currentItems.forEach(item => {
      if (item.groupUuid) {
        itemMap.set(`${item.groupUuid}-${item.storeId}`, item);
      }
    });

    nomenclatureGroups.forEach(group => {
      if (!group.name) return;
      group.selectedStoreIds.forEach(storeId => {
        const key = `${group.groupUuid}-${storeId}`;
        const existingItem = itemMap.get(key);
        if (existingItem) {
          newItems.push({ ...existingItem, name: group.name });
        } else {
          const newItem = new Item();
          newItem.name = group.name;
          newItem.storeId = storeId;
          newItem.groupUuid = group.groupUuid;
          if (reindexedStores[storeId]) {
            newItem.distCenterId = reindexedStores[storeId].DistCenterId;
          }
          // Наследуем групповые характеристики
          newItem.weight = group.weight || "";
          newItem.volume = group.volume || "";
          newItem.price = group.price || "";
          newItem.packageTypeId = group.packageTypeId || null;
          newItem.quantity = group.quantity || "";
          newItems.push(newItem);
        }
      });
    });

    const currentItemsStringified = JSON.stringify(currentItems.map(i => ({uuid: i.uuid, name: i.name, storeId: i.storeId, groupUuid: i.groupUuid})));
    const newItemsStringified = JSON.stringify(newItems.map(i => ({uuid: i.uuid, name: i.name, storeId: i.storeId, groupUuid: i.groupUuid})));
    if (currentItemsStringified !== newItemsStringified) {
        setItems(newItems);
    }
  }, [nomenclatureGroups, reindexedStores]);

  const itemCardRemoveButtonStyle = { 
    width: '50px', 
    height: '35px', 
    backgroundColor: 'red', 
    color: 'white', 
    borderRadius: '5px',
    border: 'none',
    float: 'right',
    boxShadow: 'none'
  };

  const getItemErrors = (nameField, key) => {
    return (fieldsErrors && typeof fieldsErrors['item.'+nameField+'.'+key] !== "undefined")
      ? fieldsErrors['item.'+nameField+'.'+key][0]
      : null;
  };

  const getFieldErrors = (nameField) => {
    return (fieldsErrors && typeof fieldsErrors[nameField] !== "undefined")
      ? fieldsErrors[nameField][0]
      : null;
  };

  const getNoErrors = () => null;

  const addNomenclatureGroup = () => {
    setNomenclatureGroups(prev => [...prev, createGroup()]);
  };

  const removeNomenclatureGroup = (groupUuid) => {
    setNomenclatureGroups(prev => prev.filter(g => g.groupUuid !== groupUuid));
  };

  const updateGroupName = (groupUuid, name) => {
    setNomenclatureGroups(prev => prev.map(g =>
      g.groupUuid === groupUuid ? { ...g, name } : g
    ));
  };

  const updateGroupStores = (groupUuid, selectedStoreIds) => {
    setNomenclatureGroups(prev => prev.map(g =>
      g.groupUuid === groupUuid ? { ...g, selectedStoreIds } : g
    ));
  };

  // Обновление группового поля характеристики — применяется ко всем магазинам группы
  const updateGroupField = (groupUuid, field, value) => {
    setNomenclatureGroups(prev => prev.map(g =>
      g.groupUuid === groupUuid ? { ...g, [field]: value } : g
    ));
    setItems(prevItems => prevItems.map(item =>
      item.groupUuid === groupUuid ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (uuid) => {
    const itemToRemove = items.find(item => item.uuid === uuid);
    if (!itemToRemove) return;

    const newItems = items.filter(item => item.uuid !== uuid);
    setItems(newItems);

    if (itemToRemove.groupUuid) {
      const isLastItemForStoreInGroup = !newItems.some(
        item => item.groupUuid === itemToRemove.groupUuid && item.storeId === itemToRemove.storeId
      );
      if (isLastItemForStoreInGroup) {
        setNomenclatureGroups(prev => prev.map(group => {
          if (group.groupUuid === itemToRemove.groupUuid) {
            return { ...group, selectedStoreIds: group.selectedStoreIds.filter(id => id !== itemToRemove.storeId) };
          }
          return group;
        }));
      }
    }
  };

  const updateItemField = (uuid, field, value) => {
    setItems(prevItems => 
      prevItems.map(item => 
        item.uuid === uuid ? { ...item, [field]: value } : item
      )
    );
  };

  const handleNameSearch = (groupUuid, value, skipDropdown = false) => {
    if (!debounceRefs.current[groupUuid]) {
      debounceRefs.current[groupUuid] = {};
    }
    
    if (debounceRefs.current[groupUuid].timeout) {
      clearTimeout(debounceRefs.current[groupUuid].timeout);
    }

    debounceRefs.current[groupUuid].timeout = setTimeout(() => {
      if (value && legalEntityId && !skipDropdown) {
        crossDockService.getPreset(legalEntityId, value)
          .then(res => {
            const options = res.map(item => ({
              value: item.Name,
              label: item.Name,
              id: item.Id
            }));
            setPresetOptions(prev => ({ ...prev, [groupUuid]: options }));
            if (options.length > 0) {
              setOpenDropdown(prev => ({ ...prev, [groupUuid]: true }));
            }
          })
          .catch((err) => {
            console.error('getPreset error:', err);
            setPresetOptions(prev => ({ ...prev, [groupUuid]: [] }));
          });
      } else {
        setPresetOptions(prev => ({ ...prev, [groupUuid]: [] }));
      }
    }, 500);
  };

  const handlePresetSelect = (groupUuid, value, option) => {
    if (!debounceRefs.current[groupUuid]) {
      debounceRefs.current[groupUuid] = {};
    }
    debounceRefs.current[groupUuid].selecting = true;
    debounceRefs.current[groupUuid].selectedPresetId = option.id;
    
    setOpenDropdown(prev => ({ ...prev, [groupUuid]: false }));
    
    if (debounceRefs.current[groupUuid].timeout) {
      clearTimeout(debounceRefs.current[groupUuid].timeout);
    }
    
    updateGroupName(groupUuid, value);

    setTimeout(() => {
      if (debounceRefs.current[groupUuid]) {
        debounceRefs.current[groupUuid].selecting = false;
      }
    }, 300);
  };

  const handleNomenclatureInputChange = (groupUuid, value) => {
    updateGroupName(groupUuid, value);
    if (value) {
      handleNameSearch(groupUuid, value);
    } else {
      setPresetOptions(prev => ({ ...prev, [groupUuid]: [] }));
      setOpenDropdown(prev => ({ ...prev, [groupUuid]: false }));
    }
  };

  const getItemsForGroup = (groupUuid) => {
    return items.filter(item => item.groupUuid === groupUuid);
  };

  const renderGroupItems = (groupItems) => {
    return groupItems.map((cont) => (
      <div style={{ border: '1px solid rgba(0, 0, 0, 0.1)', marginTop: '10px', padding: '15px', borderRadius: '5px' }} key={`item-${cont.uuid}`}>
        <Row gutter={[16, 24]} >
          <Col lg={20} md={24} sm={24} sx={24}>
            <FieldBlock
              name="store"
              getErrors={getItemErrors}
              keyNum={cont.uuid}
            >
              <span>{(storesList.find(s => s.Id === cont.storeId) || {}).Name || cont.storeId}</span>
            </FieldBlock>
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <Button
                type="primary"
                danger
                icon="close"
                onClick={() => removeItem(cont.uuid)}
                style={itemCardRemoveButtonStyle}
            />
          </Col>
        </Row>
        <Row gutter={[16, 24]} style={{ marginTop: '15px' }}>
          <Col lg={4} md={24} sm={24} sx={24}>
              <NumberInputField
                  name="weight" label="Вес (кг) (1 ед.)"
                  value={cont.weight} setValue={value => updateItemField(cont.uuid, 'weight', value)}
                  keyNum={cont.uuid}
                  getFieldErrors={getItemErrors}
              />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
              <NumberInputField
                  name="volume" label="Объём (м³) (1 ед.)"
                  value={cont.volume} setValue={value => updateItemField(cont.uuid, 'volume', value)}
                  keyNum={cont.uuid}
                  getFieldErrors={getItemErrors}
                  step={0.01}
              />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
              <NumberInputField
                  name="price" label="Оц. ст. (1 ед.)"
                  value={cont.price} setValue={value => updateItemField(cont.uuid, 'price', value)}
                  keyNum={cont.uuid}
                  getFieldErrors={getItemErrors}
              />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <SelectField
              title="Тип упаковки"
              name="packageTypeId"
              allowClear={true}
              selectedValue={cont.packageTypeId}
              values={packageTypeOptions}
              keyNum={cont.uuid}
              onChange={value => updateItemField(cont.uuid, 'packageTypeId', value)}
              getErrors={getItemErrors}
              style={{ width: "100%" }}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
              <NumberInputField
                  name="quantity" label="Количество"
                  value={cont.quantity} setValue={value => updateItemField(cont.uuid, 'quantity', value)}
                  keyNum={cont.uuid}
                  getFieldErrors={getItemErrors}
              />
          </Col>
        </Row>
      </div>
    ));
  };

  const renderGroup = (group) => {
    const groupItems = getItemsForGroup(group.groupUuid);

    return (
      <div key={group.groupUuid} style={{ marginBottom: '30px', border: '1px solid #d9d9d9', padding: '20px', borderRadius: '8px' }}>
        <Row gutter={[16, 24]} style={{ marginBottom: '16px' }}>
          <Col lg={20} md={24} sm={24} sx={24}>
            <FieldBlock name="nomenclature" title={i18n.t('Наименование номенклатуры')} getErrors={getFieldErrors}>
              <AutoComplete
                value={group.name}
                filterOption={false}
                open={openDropdown[group.groupUuid] || false}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                onDropdownVisibleChange={(visible) => {
                  if (!debounceRefs.current[group.groupUuid] || !debounceRefs.current[group.groupUuid].selecting) {
                    setOpenDropdown(prev => ({ ...prev, [group.groupUuid]: visible }));
                  }
                }}
                onChange={(value) => handleNomenclatureInputChange(group.groupUuid, value)}
                onSelect={(value, option) => handlePresetSelect(group.groupUuid, value, option)}
                style={{ width: "100%" }}
                placeholder={i18n.t('Наименование номенклатуры')}
              >
                {(presetOptions[group.groupUuid] || []).map((opt) => (
                  <AutoComplete.Option key={opt.id} value={opt.value} id={opt.id}>
                    {opt.label}
                  </AutoComplete.Option>
                ))}
              </AutoComplete>
            </FieldBlock>
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <Button
              type="primary"
              danger
              icon="close"
              onClick={() => removeNomenclatureGroup(group.groupUuid)}
              style={itemCardRemoveButtonStyle}
            />
          </Col>
        </Row>

        <Row gutter={[16, 24]} style={{ marginBottom: '16px' }}>
          <Col lg={4} md={24} sm={24} sx={24}>
            <NumberInputField
              name="weight" label="Вес (кг) (1 ед.)"
              value={group.weight}
              setValue={value => updateGroupField(group.groupUuid, 'weight', value)}
              getFieldErrors={getNoErrors}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <NumberInputField
              name="volume" label="Объём (м³) (1 ед.)"
              value={group.volume}
              setValue={value => updateGroupField(group.groupUuid, 'volume', value)}
              getFieldErrors={getNoErrors}
              step={0.01}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <NumberInputField
              name="price" label="Оц. ст. (1 ед.)"
              value={group.price}
              setValue={value => updateGroupField(group.groupUuid, 'price', value)}
              getFieldErrors={getNoErrors}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <SelectField
              title="Тип упаковки"
              name="packageTypeId"
              allowClear={true}
              selectedValue={group.packageTypeId}
              values={packageTypeOptions}
              onChange={value => updateGroupField(group.groupUuid, 'packageTypeId', value)}
              getErrors={getNoErrors}
              style={{ width: "100%" }}
            />
          </Col>
          <Col lg={4} md={24} sm={24} sx={24}>
            <NumberInputField
              name="quantity" label="Количество"
              value={group.quantity}
              setValue={value => updateGroupField(group.groupUuid, 'quantity', value)}
              getFieldErrors={getNoErrors}
            />
          </Col>
        </Row>

        <Row gutter={[16, 24]} style={{ marginBottom: '16px' }}>
          <Col lg={24} md={24} sm={24} sx={24}>
            <FieldBlock name="stores" title="Магазины" getErrors={getFieldErrors}>
              <div style={{ marginBottom: '8px' }}>
                <Checkbox
                  checked={group.selectedStoreIds.length === storesList.length && storesList.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateGroupStores(group.groupUuid, storesList.map(s => s.Id));
                    } else {
                      updateGroupStores(group.groupUuid, []);
                    }
                  }}
                >
                  Выбрать все
                </Checkbox>
              </div>
              <SelectField
                name="selectedStores"
                selectedValue={group.selectedStoreIds}
                values={storesList}
                onChange={(values) => updateGroupStores(group.groupUuid, values)}
                getErrors={getFieldErrors}
                style={{ width: "100%" }}
                mode="multiple"
                placeholder="Выберите магазины"
              />
            </FieldBlock>
          </Col>
        </Row>

        {renderGroupItems(groupItems)}
      </div>
    );
  };

  return (
    <>
      <span className="divider-label">Номенклатура</span>
      <DividerBlock style={dividerStyle}/>
      {fieldsErrors && fieldsErrors['items'] && (
        <Alert
          message={fieldsErrors['items'][0]}
          type="error"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}
      {nomenclatureGroups.map(group => renderGroup(group))}

      <Row gutter={[16, 24]} style={{ marginBottom: '24px' }}>
        <Col lg={24} md={24} sm={24} sx={24}>
          <Button
            type="primary"
            onClick={addNomenclatureGroup}
            disabled={storesList.length === 0}
            style={{ width: '100%', height: '43px', background: '#1CAA53', borderRadius: '10px', borderColor: 'transparent', boxShadow: 'none' }}
            icon="plus"
          >
            Добавить номенклатуру
          </Button>
        </Col>
      </Row>

      {(showWarning) && 
          <Alert
            message="Выбранные магазины относятся к нескольким РЦ"
            description="Будет создано несколько заявок. Поставка должна быть на каждый распределительный центр."
            type="warning"
            showIcon
            style={{ marginTop: '20px', marginBottom: '20px' }}
          />
      }
      <DividerBlock style={dividerStyle} />
    </>
  );
}